import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { motion } from "framer-motion";

interface MallHubSectionProps {
  icon: React.ReactNode;
  iconBackground: string;
  title: string;
  subtitle: string;
  onViewAll?: () => void;
  children: React.ReactNode;
  delay?: number;
}

/**
 * MallHubSection Component
 *
 * Reusable section wrapper with header, icon, and optional "View All" button
 */
const MallHubSection: React.FC<MallHubSectionProps> = ({
  icon,
  iconBackground,
  title,
  subtitle,
  onViewAll,
  children,
  delay = 0,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
    >
      <Box sx={{ mb: 6 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                background: iconBackground,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            </Box>
          </Box>
          {onViewAll && (
            <Button
              variant="outlined"
              endIcon={<ArrowForward />}
              onClick={onViewAll}
              sx={{ display: { xs: "none", sm: "flex" } }}
            >
              View All
            </Button>
          )}
        </Box>

        {children}

        {/* Mobile View All Button */}
        {onViewAll && (
          <Box sx={{ display: { xs: "block", sm: "none" }, mt: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              endIcon={<ArrowForward />}
              onClick={onViewAll}
            >
              View All {title}
            </Button>
          </Box>
        )}
      </Box>
    </motion.div>
  );
};

export default MallHubSection;
